import React from "react";
import { motion } from "framer-motion";
import {
  Film,
  Video,
  Wand2,
  Camera,
  Hash,
  PenTool,
  Sparkles,
  Palette,
  Search,
  ArrowUpRight,
} from "lucide-react";
import { SERVICES } from "../data/site";

const ICONS = { Film, Video, Wand2, Camera, Hash, PenTool, Sparkles, Palette, Search };

const BG_VIDEO =
  "https://images.unsplash.com/photo-1769699167906-a44d5ae50abb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHx2aWRlb2dyYXBoeSUyMGNhbWVyYSUyMHN0dWRpb3xlbnwwfHx8fDE3NzcyOTIwOTR8MA&ixlib=rb-4.1.0&q=85";
const BG_PHOTO =
  "https://images.unsplash.com/photo-1765383563504-63f95cab942c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHx2aWRlb2dyYXBoeSUyMGNhbWVyYSUyMHN0dWRpb3xlbnwwfHx8fDE3NzcyOTIwOTR8MA&ixlib=rb-4.1.0&q=85";
const BG_SOCIAL =
  "https://images.pexels.com/photos/14540962/pexels-photo-14540962.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BG_CONTENT =
  "https://images.pexels.com/photos/28583190/pexels-photo-28583190.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Bento placement map (Tetris). Each service takes different spans on lg.
const GRID_CLASS = {
  "video-editing": "lg:col-span-7 lg:row-span-2",
  videography: "lg:col-span-5 lg:row-span-1",
  photography: "lg:col-span-5 lg:row-span-1",
  "photo-editing": "lg:col-span-4 lg:row-span-1",
  "social-media": "lg:col-span-4 lg:row-span-2",
  copywriting: "lg:col-span-4 lg:row-span-1",
  "content-creation": "lg:col-span-4 lg:row-span-1",
  "graphic-design": "lg:col-span-4 lg:row-span-1",
  seo: "lg:col-span-4 lg:row-span-1",
};

const BG_IMAGE = {
  "video-editing": BG_VIDEO,
  photography: BG_PHOTO,
  "social-media": BG_SOCIAL,
  "content-creation": BG_CONTENT,
};

function ServiceCard({ svc, idx }) {
  const Icon = ICONS[svc.icon] || Sparkles;
  const bg = BG_IMAGE[svc.id];
  const isFeatured = Boolean(bg);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: idx * 0.04 }}
      className={`bento-card group relative p-8 md:p-10 min-h-[240px] flex flex-col justify-between ${
        GRID_CLASS[svc.id] || "lg:col-span-4"
      }`}
      data-testid={`service-card-${svc.id}`}
    >
      {isFeatured && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-40 group-hover:opacity-60 transition-opacity duration-700"
            style={{ backgroundImage: `url(${bg})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/60 to-black/30" />
        </>
      )}

      <div className="relative z-10 flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 font-mono">
          — {svc.tag}
        </div>
        <div className="w-10 h-10 border border-white/10 flex items-center justify-center group-hover:border-[#FF3B30] group-hover:text-[#FF3B30] transition-all">
          <Icon size={18} />
        </div>
      </div>

      <div className="relative z-10 mt-10">
        <h3 className="font-display text-3xl md:text-4xl font-extrabold uppercase tracking-tight leading-[0.95]">
          {svc.title}
        </h3>
        <p className="mt-4 text-sm md:text-base text-white/60 leading-relaxed max-w-md">
          {svc.description}
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/70 group-hover:text-[#FF3B30] transition-colors">
          {svc.short}
          <ArrowUpRight size={14} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </div>
      </div>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-black"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4">
              — What we do
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9]">
              Nine services.
              <br />
              <span className="text-outline-red">One obsessive</span> team.
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-base md:text-lg leading-relaxed">
            From the first storyboard to the last pixel — we own the whole
            stack so your brand ships faster, louder, sharper.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 lg:auto-rows-[240px] gap-4 md:gap-5">
          {SERVICES.map((svc, i) => (
            <ServiceCard key={svc.id} svc={svc} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
